/**
 * workflowCtaLifecycle — ONE derived truth for whether a special-element
 * workflow CTA row is still actionable (owner directive 2026-08-19).
 *
 * Special-element buttons (clarify skip/answer, translate, video script,
 * start research, payment gate, legal-doc pickers, connect-app, …) must be
 * GREYED OUT AND UNCLICKABLE the moment the agentic workflow has started
 * past their card — and stay dead forever after, surviving refresh and
 * rehydrate. A stale CTA click can fork a live pipeline (the class behind
 * the PROD-185 Regenerate crash and mid-run clarify-skip forks).
 *
 * The rule is LATEST-ACTIONABLE-ONLY, derived (never persisted):
 *   a message's CTA row is live iff
 *     • the message is the NEWEST visible (non-context-only) message in the
 *       thread — anything newer (a user reply, a pending widget, a delivered
 *       report) means the process moved past the card — AND
 *     • the thread has no in-flight turn (the click that advanced the
 *       workflow flips processing before any newer message lands).
 *
 * Because the rule derives from the transcript + the processing flag, an old
 * card is dead after refresh with no bookkeeping, and the one live card
 * re-arms automatically when a turn fails before producing anything newer.
 * OVER-retiring is the intended failure direction: a dead button on a card
 * the user scrolled back to is mildly annoying; a live button on a
 * superseded card forks production workflows.
 *
 * Utility buttons (copy / download / preview / stop / language toggle) are
 * NOT workflow CTAs and are never retired: the html transform below skips
 * the exemption registry even on the retire-everything path, so a Stop
 * button can never be greyed out by this module.
 */

import type { ChatMessage } from './types';
import { SURFACE_SERIES_COMPOSITION } from '../../services/globalTools/featureFlags';

// ── CTA id registry ─────────────────────────────────────────────────────────
// Buttons that ADVANCE a workflow. Buttons inside `workflowActionButtonsHtml`
// are all CTAs by construction (use retireAllButtonsHtml there); the exact
// ids + patterns below additionally cover CTAs minted into message-body html.
// Ids arrive via the `id` attribute (legacy) OR `data-button-id` (browser
// tiles) — the transform reads both, mirroring the delegated click handler.
const WORKFLOW_CTA_IDS = new Set<string>([
  'task-queue-dr-clarifying-skip-btn',
  'task-queue-dr-clarifying-answer-btn',
  'global-tool-deep-research-skip-btn',
  'global-tool-deep-research-start-btn',
  'global-tool-deep-research-video-script-btn',
  'global-tool-video-script-btn',
  'global-tool-video-script-proceed-btn',
  'global-tool-translate-btn',
  'global-tool-format-report-btn',
  'global-tool-document-analyzer-retry-btn',
  'global-tool-multi-doc-manifest-btn',
  'global-tool-legal-doc-creator-btn',
  'global-tool-composio-action-btn',
  'payment-gate-pay-btn',
  'payment-gate-cancel-btn',
  'browser-use-submit-answer',
  'clarifying-answer-btn',
]);

const WORKFLOW_CTA_ID_PATTERNS: RegExp[] = [
  // Tool CTA namespace (covers per-app suffixed ids like
  // `global-tool-connect-app-btn-slack`).
  /^global-tool-/,
  // Video-script flow families (stage buttons carry stage suffixes).
  /^chat-write-video-script/,
  /^chat-video-script/,
  /^chat-format-report/,
  // Translate family (`translate-btn` + per-language suffixes).
  /^translate-.*btn$/i,
  // Legal-doc template pickers (`legal-doc-nda-btn`, …).
  /^legal-doc-.*-btn$/,
  // Explicit opt-in convention for future CTAs.
  /-cta-btn$/,
];

// NEVER retired — stop/interrupt controls and pure utilities. Checked before
// the registry AND inside the retire-everything path, so no caller can grey
// out a Stop button through this module.
const WORKFLOW_CTA_EXEMPT_IDS = new Set<string>([
  'pending-widget-stop-btn',
  'pending-stop-btn',
  'browser-use-stop-session',
  'browser-use-view-desktop',
  'browser-use-open-deep-link',
  'browser-use-lang-toggle',
  'chunk-copy-btn',
  'legal-doc-save-as-template-btn',
]);

// Pattern-shaped exemptions for per-instance suffixed ids (owner screenshots
// 2026-08-19): the outward-send authorize card ("send it" / "don't send",
// ids suffixed with the actionId) is a STANDING DECISION valid for 24h
// server-side, not a workflow step a newer message supersedes — retiring it
// on the next message made the card silently unclickable the moment
// Prest0n's own reply streamed in after it, so authorizations went nowhere.
// The server's transactional claim is the real guard: a stale card's click
// now gets an honest already-decided/expired outcome message instead of a
// dead button.
const WORKFLOW_CTA_EXEMPT_PATTERNS: RegExp[] = [
  // `retry` joined 2026-08-29 (adversarial review): it renders only on a
  // receipt whose failure PROVED nothing left the building and whose hold is
  // still armed — the same 24h standing decision as approve/cancel. With the
  // receipt now stamped onto the ORIGINAL card message (one-tile directive),
  // a non-exempt retry would grey the moment any newer message existed.
  /^authorize-action-(approve|cancel|retry)/,
  // Batch send tile (2026-08-27, Justin's six-emails report): the batch
  // card is the SAME standing decision as the single authorize card, once
  // per row plus a master "send all remaining". Both stay pressable for the
  // hold's 24h server window — the per-doc transactional claim answers a
  // stale press honestly, and the tile re-renders in place long after newer
  // messages land, so a newest-message retirement would kill live rows
  // mid-batch. (The per-row preview toggle was retired 2026-08-29: action
  // tiles carry no preview windows.)
  /^batch-send-all-/,
  // The jump rail (2026-09-17, owner: "it should be able to easily quickly
  // view all emails suggested within the element"): one pill per staged row
  // that scrolls the stack to it. It decides nothing and touches no network —
  // it is navigation WITHIN a card that stays live for the hold's 24h window,
  // so retiring it on a newer message would strand the reader in a long
  // stack with no way back to the row they were reading.
  /^batch-jump-/,
  // Connect-offer cards are STANDING OFFERS, not workflow steps (owner
  // incident 2026-08-20, RingCentral): the spurious step-limit bubble after
  // an offer turn landed as a newer message and permanently killed the
  // card's sign-in button. Connecting an app is valid whenever the user
  // gets to it; the real belts are click-time — the delegated handler's
  // disabled/CTA_RETIRED checks, processingButtonsRef's double-click guard,
  // the already-connected roster checks in offerConnectForQuery, and OAuth
  // mint idempotency. Covers the CONNECT_APP_BUTTON_IDS families:
  // …-btn-<slug> (OAuth connect / add-another), …-browser-btn-open
  // (browser lane), …-reopen-btn-<slug> (popup-blocked reopen),
  // …-alt-btn-<kind>-<id> (alternative pills inside the connect card's
  // "other ways ▾" disclosure, plan §9.5a, 2026-09-02) and
  // …-answer-btn-<option>-<id> (P1a question options, §9.4a — retired and
  // ✓-painted from the intent RECORD, never from message order). The
  // selection and retire transforms walk EVERY <button> in the html, so
  // without these two families the alternatives would be painted or
  // retired together with the primary.
  /^global-tool-connect-app-(btn|browser-btn|reopen-btn|alt-btn|answer-btn)(-|$)/,
];

export function isWorkflowCtaExemptId(id: string | null | undefined): boolean {
  const v = (id || '').trim();
  if (!v) return false;
  if (WORKFLOW_CTA_EXEMPT_IDS.has(v)) return true;
  return WORKFLOW_CTA_EXEMPT_PATTERNS.some((re) => re.test(v));
}

export function isWorkflowCtaButtonId(id: string | null | undefined): boolean {
  const v = (id || '').trim();
  if (!v) return false;
  if (isWorkflowCtaExemptId(v)) return false;
  if (WORKFLOW_CTA_IDS.has(v)) return true;
  return WORKFLOW_CTA_ID_PATTERNS.some((re) => re.test(v));
}

// ── Liveness rule ───────────────────────────────────────────────────────────

export interface WorkflowCtaLivenessParams {
  /** Id of the message that carries the CTA row. */
  messageId: string | null | undefined;
  /** The thread's transcript, oldest first (rendered state for the pane). */
  messages: ReadonlyArray<ChatMessage> | undefined;
  /** The thread's in-flight processing flag (per-thread, derived). */
  threadProcessing: boolean;
}

/**
 * True while the CTA row on `messageId` may still be pressed. See the module
 * docblock for the rule. Unknown message / empty transcript ⇒ NOT live
 * (never enable a button we cannot place in the timeline).
 *
 * Surface-plan P2 (2026-08-29, flag-gated): with SURFACE_SERIES_COMPOSITION
 * on, a newer message that is a SERIES SIBLING of the card's own turn — it
 * carries a `surfaceSeriesAnchor` equal to the card message's anchor
 * identity (its taskAnchorId, its own id, or its own series anchor) — does
 * not retire the card: a file card or receipt delivered in the same turn is
 * part of the same series, not the process moving past it. ONLY stamped
 * siblings are skipped; any unstamped newer message (a user reply, a normal
 * assistant bubble, a pending widget) still retires exactly as before, so
 * over-retiring remains the intended failure direction and, with no
 * emitters stamping the anchor yet, flag-on is behaviorally identical to
 * flag-off.
 */
export function isWorkflowCtaLive(params: WorkflowCtaLivenessParams): boolean {
  const { messageId, messages, threadProcessing } = params;
  if (threadProcessing) return false;
  if (!messageId || !Array.isArray(messages) || messages.length === 0) return false;
  const ownAnchor = SURFACE_SERIES_COMPOSITION
    ? (() => {
        const own = messages.find((m) => m && m.id === messageId);
        if (!own) return '';
        return String(own.taskAnchorId || own.surfaceSeriesAnchor || own.id || '').trim();
      })()
    : '';
  // Newest visible message wins. Walk from the tail; the first visible
  // message decides — if it isn't ours, the process moved past the card.
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (!m || m.isContextOnly) continue;
    if (
      SURFACE_SERIES_COMPOSITION
      && ownAnchor
      && m.id !== messageId
      && typeof m.surfaceSeriesAnchor === 'string'
      && m.surfaceSeriesAnchor.trim() === ownAnchor
    ) {
      // Same turn's surface series — does not supersede the card.
      continue;
    }
    return m.id === messageId;
  }
  return false;
}

// ── HTML transform ──────────────────────────────────────────────────────────

/** Marker attribute so the transform is idempotent and the click dispatcher /
 *  restore helpers can recognize a retired button without recomputing
 *  liveness. */
export const CTA_RETIRED_ATTR = 'data-cta-retired';

const RETIRED_STYLE =
  'pointer-events:none;opacity:0.45;filter:grayscale(0.7);cursor:not-allowed;box-shadow:none;';

const BUTTON_TAG_RE = /<button\b([^>]*)>/gi;

function extractAttr(attrs: string, name: string): string | null {
  const m = new RegExp(`${name}\\s*=\\s*"([^"]*)"`, 'i').exec(attrs);
  return m ? m[1] : null;
}

/**
 * Rewrite `<button …>` open-tags in an injected-HTML string so matching
 * buttons render greyed out and cannot be pressed (native `disabled` +
 * pointer-events + tabindex). `shouldRetire` decides per button id; pass
 * `() => true` for `workflowActionButtonsHtml` (all CTAs by construction)
 * and `isWorkflowCtaButtonId` for message-body html so utility buttons
 * (copy / download / preview) are never touched. Stop/utility exemptions are
 * enforced HERE, before `shouldRetire`, so no predicate can grey them out.
 * Idempotent.
 */
export function retireWorkflowCtaButtonsHtml(
  html: string | null | undefined,
  shouldRetire: (id: string | null) => boolean,
): string {
  const input = html || '';
  if (!input || !input.toLowerCase().includes('<button')) return input;
  return input.replace(BUTTON_TAG_RE, (tag, attrs: string) => {
    if (attrs.includes(CTA_RETIRED_ATTR)) return tag;
    const id = extractAttr(attrs, 'id') ?? extractAttr(attrs, 'data-button-id');
    if (isWorkflowCtaExemptId(id)) return tag;
    if (!shouldRetire(id)) return tag;
    let nextAttrs = attrs;
    const style = extractAttr(attrs, 'style');
    if (style !== null) {
      const merged = `${style}${style.trim().endsWith(';') || !style.trim() ? '' : ';'}${RETIRED_STYLE}`;
      nextAttrs = nextAttrs.replace(/style\s*=\s*"[^"]*"/i, `style="${merged}"`);
    } else {
      nextAttrs = `${nextAttrs} style="${RETIRED_STYLE}"`;
    }
    return `<button${nextAttrs} disabled aria-disabled="true" tabindex="-1" ${CTA_RETIRED_ATTR}="true">`;
  });
}

/** Convenience for the CTA row container: retire every button (stop/utility
 *  exemptions still apply — enforced inside the transform). */
export function retireAllButtonsHtml(html: string | null | undefined): string {
  return retireWorkflowCtaButtonsHtml(html, () => true);
}

// ── Click-restore gate ──────────────────────────────────────────────────────

/**
 * Re-enable a CTA button after its OWN action failed — but only while its
 * card is still the live one. Click handlers disable the DOM node they were
 * handed and restore it on failure; if the render transform retired the card
 * in the meantime (the action advanced the workflow before failing, or a
 * newer message landed), the button stays dead. Use this instead of raw
 * `buttonEl.disabled = false` in every workflow-CTA failure/retry path.
 */
export function restoreWorkflowCtaButton(
  buttonEl: Element | null | undefined,
): void {
  if (!buttonEl) return;
  if (buttonEl.getAttribute(CTA_RETIRED_ATTR) === 'true') return;
  (buttonEl as HTMLButtonElement).disabled = false;
}

/**
 * Platform CTA tokens, mirrored from agenticWorkflows/helpers (importing the
 * service module here would pull the whole workflow layer into the chat
 * component graph — mirrored VALUES with a parity test instead:
 * workflowCtaLifecycle.test.ts pins these against the helpers' exports).
 */
export const LEGACY_CTA_PRIMARY_BG = '#8A6D2F';
export const LEGACY_CTA_SECONDARY_BG = '#6B6459';

/**
 * Off-palette CTA hexes that shipped inside PERSISTED button html before the
 * 2026-08-22 palette directive (blue deep-research, green write-video-script,
 * purple translate, sky save-template, violet translate-to-spanish). The html
 * rides Firestore, so every historical message would keep its off-palette
 * buttons forever without a render-time normalization.
 */
const LEGACY_CTA_COLOR_MAP: Record<string, string> = {
  '#1e40af': LEGACY_CTA_PRIMARY_BG,
  '#059669': LEGACY_CTA_SECONDARY_BG,
  '#7C3AED': LEGACY_CTA_PRIMARY_BG,
  '#0369a1': LEGACY_CTA_SECONDARY_BG,
  '#8B5CF6': LEGACY_CTA_PRIMARY_BG,
};

/**
 * Recolor legacy persisted CTA html onto the platform scheme. Owner directive
 * (2026-08-22): if buttons ever show, they line up with the special-element
 * color scheme — including buttons stored years ago. Idempotent; leaves
 * non-button html alone (the hexes only ever appeared as CTA backgrounds and
 * their box-shadow rgba twins are left to the box-shadow, which reads fine
 * over the recolored background).
 */
export function normalizeLegacyCtaColors(html: string | null | undefined): string {
  if (!html) return '';
  let out = html;
  for (const [legacy, token] of Object.entries(LEGACY_CTA_COLOR_MAP)) {
    // Colon-prefixed only (adversarial review 2026-08-29): the legacy hexes
    // only ever appeared as inline-style VALUES (`background:#1e40af`),
    // while cards now carry the user's full draft body as escaped text — a
    // draft that merely mentions "#1e40af" must render exactly as written.
    for (const hex of [legacy, legacy.toLowerCase()]) {
      out = out.split(`:${hex}`).join(`:${token}`);
      out = out.split(`: ${hex}`).join(`: ${token}`);
    }
  }
  return out;
}

/**
 * Applies visual selection state to workflow action buttons HTML.
 * - Selected button gets green background with checkmark
 * - Other buttons get greyed out and disabled
 *
 * @param html - The workflow action buttons HTML string
 * @param selectedIndex - Index of the selected button (undefined if none selected)
 * @returns Modified HTML with selection styling applied
 */
export function applyButtonSelectionState(html: string, selectedIndex?: number): string {
  if (selectedIndex === undefined) return html;

  // Use DOMParser to safely modify the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const container = doc.body.firstChild as HTMLElement;

  if (!container) return html;

  const buttons = container.querySelectorAll('button');
  buttons.forEach((btn, index) => {
    // Standing-decision surfaces (authorize/batch cards, connect offers) are
    // EXEMPT from the one-choice paint (2026-08-27, Miles's live repro): a
    // preview click used to persist a wrong selectedActionButtonIndex that
    // re-greyed the whole card on every render. The click lane no longer
    // writes an index for exempt ids, and this render-side skip HEALS the
    // messages already carrying a poisoned index — their card html stays
    // exactly as the card module rendered it, no migration needed.
    if (isWorkflowCtaExemptId(btn.getAttribute('id') || btn.getAttribute('data-button-id'))) {
      return;
    }
    if (index === selectedIndex) {
      // Selected state, brand-consistent (2026-08-12): ink button with a gold
      // ring + checkmark — the old emerald green was a one-off hue, and its
      // checkmark logic only worked on buttons wrapping a <span> (the connect
      // card's buttons don't), so users saw a bare green pill with no ✓.
      btn.style.background = '#1C1917';
      btn.style.boxShadow = '0 0 0 2px var(--brand-gold), 0 8px 20px rgba(198, 167, 94, 0.35)';
      btn.style.cursor = 'pointer';  // Keep clickable (was 'default')
      const span = btn.querySelector('span');
      if (span) {
        span.innerHTML = `&#10003; ${span.textContent || ''}`;
      } else if (!(btn.textContent || '').trimStart().startsWith('✓')) {
        btn.innerHTML = `&#10003; ${btn.innerHTML}`;
      }
    } else {
      // Slightly dim unselected buttons but keep them clickable for cross-device functionality
      btn.style.opacity = '0.7';  // Less dimmed (was 0.4) for better visibility
      btn.style.cursor = 'pointer';  // Keep clickable (was 'not-allowed')
      // Removed: btn.style.pointerEvents = 'none' - buttons stay clickable across devices
      btn.style.filter = 'grayscale(30%)';  // Less greyscale (was 50%)
    }
  });

  return container.innerHTML;
}
